import type { ForkOverridesShape } from './fire-forks-types';

function isMeaningfulMultiplier(value: number | undefined): boolean {
  return value !== undefined && value !== 1;
}

function hasPersonChanges(overrides: ForkOverridesShape): boolean {
  return [
    overrides.persons?.primary?.state,
    overrides.persons?.primary?.taxLocality,
    overrides.persons?.spouse?.state,
    overrides.persons?.spouse?.taxLocality,
  ].some((value) => value !== undefined);
}

export function hasRunnableForkOverrides(
  overrides: ForkOverridesShape | null | undefined,
): boolean {
  if (!overrides) return false;
  const scalars = overrides.scalars ?? {};
  return (
    isMeaningfulMultiplier(scalars.discretionaryMultiplier) ||
    isMeaningfulMultiplier(scalars.wageMultiplier) ||
    isMeaningfulMultiplier(scalars.incomeMultiplier) ||
    isMeaningfulMultiplier(scalars.holdingsMultiplier) ||
    (scalars.targetFireYearDelta ?? 0) !== 0 ||
    hasPersonChanges(overrides) ||
    (overrides.droppedIncomeStreamIds ?? []).length > 0 ||
    (overrides.excludedLifeEventIds ?? []).length > 0 ||
    (overrides.addedLifeEvents ?? []).length > 0 ||
    (overrides.addedIncomeStreams ?? []).length > 0 ||
    (overrides.incomeStreamPauses ?? []).length > 0 ||
    (overrides.holdingPriceOverrides ?? []).length > 0 ||
    Object.keys(overrides.taxStrategy ?? {}).length > 0
  );
}

export function getScenarioApplySupport(
  overrides: ForkOverridesShape | null | undefined,
): {
  canApply: boolean;
  hasSupportedChanges: boolean;
  hasSpeculativeMarketChanges: boolean;
  hasUnsupportedPlanChanges: boolean;
} {
  if (!overrides) {
    return {
      canApply: false,
      hasSupportedChanges: false,
      hasSpeculativeMarketChanges: false,
      hasUnsupportedPlanChanges: false,
    };
  }

  const scalars = overrides.scalars ?? {};
  const hasSpeculativeMarketChanges =
    isMeaningfulMultiplier(scalars.holdingsMultiplier) ||
    (overrides.holdingPriceOverrides ?? []).length > 0;
  const hasUnsupportedPlanChanges =
    (overrides.incomeStreamPauses ?? []).length > 0;
  const hasSupportedChanges =
    isMeaningfulMultiplier(scalars.discretionaryMultiplier) ||
    isMeaningfulMultiplier(scalars.wageMultiplier) ||
    isMeaningfulMultiplier(scalars.incomeMultiplier) ||
    (scalars.targetFireYearDelta ?? 0) !== 0 ||
    hasPersonChanges(overrides) ||
    (overrides.droppedIncomeStreamIds ?? []).length > 0 ||
    (overrides.excludedLifeEventIds ?? []).length > 0 ||
    (overrides.addedLifeEvents ?? []).length > 0 ||
    (overrides.addedIncomeStreams ?? []).length > 0 ||
    hasUnsupportedPlanChanges ||
    Object.keys(overrides.taxStrategy ?? {}).length > 0;

  return {
    canApply:
      hasSupportedChanges &&
      !hasSpeculativeMarketChanges &&
      !hasUnsupportedPlanChanges,
    hasSupportedChanges,
    hasSpeculativeMarketChanges,
    hasUnsupportedPlanChanges,
  };
}
